// PHASE_1 / B1 — the file-leave feature screen (wraps the B0 LeaveFormContent).
//
// LeaveFilePage is the host that wires the shell-agnostic LeaveFormContent's
// `onSubmit` to the real write path: POST /v1/leave/requests (file as draft) →
// POST /v1/leave/requests/:id/submit (submit + conflict check). The submit
// response carries the conflict WARNING (teammate overlaps) which we render
// inline — overlaps are a warning, not a rejection (B1 ships warning-only;
// LEAVE_CONFLICT_AUTO_REJECT default off). The SAME LeaveFormContent renders
// under the single parameterized AppShell in both the self (`me-leave`) and
// admin contexts (ADR-014 §2) — this page is the routed child.
//
// Hours are DECIMALS (`_hours`), never money (Phase 1 money/hours boundary).

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck2, ChevronLeft, ChevronRight, Clock3, UserRound } from "lucide-react";
import { Link as RouterLink } from "react-router";

import { Alert, Card, CardBody } from "@novemai/components";

import { platformGraph } from "@/api/platform-graph";
import { errorMessage, unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import { DIRECTORY_KEY, fetchDirectoryFirstPage } from "@/features/employees/directory-api";
import {
  LeaveFormContent,
  type LeaveRequestDraft,
} from "@/content/leave-form-content";
import { useTabTitle } from "@/components/TabStrip";

import { formatLeaveSpan } from "./leave-days";
import {
  fetchLeaveBalancesPage,
  fetchLeaveTypes,
  fileLeaveRequest,
  previewLeaveRequest,
  submitLeaveRequest,
  type ConflictOverlap,
  type FileLeaveInput,
} from "./leave-api";

// Screen-local /v1/me fetcher (the A2 dashboard pattern) — resolves the
// caller's own employee id so the side card shows OWN balances only (the
// admin hat's balances window may carry other employees' rows).
interface MeIdentity {
  readonly employee_id: string | null;
}

/** Display fallback when the policy-name join came back unresolved. */
const shortId = (id: string): string => id.slice(0, 8);

type MeGet = (path: "/v1/me") => Promise<EnvelopedFetchResult<MeIdentity>>;

async function fetchMe(): Promise<MeIdentity> {
  const get = platformGraph.GET as unknown as MeGet;
  return unwrapEnvelope(get("/v1/me"));
}

interface PendingFileCreate {
  readonly fingerprint: string;
  readonly idempotencyKey: string;
}

interface PendingDraft {
  readonly requestId: string;
  readonly fingerprint: string;
}

interface PendingSubmit {
  readonly requestId: string;
  readonly idempotencyKey: string;
}

function toFileLeaveInput(draft: LeaveRequestDraft): FileLeaveInput {
  return {
    policy_id: draft.policy_id,
    start_date: draft.start_date,
    end_date: draft.end_date,
    ...(draft.partial_days !== undefined && { partial_days: draft.partial_days }),
    ...(draft.reason !== "" && { reason: draft.reason }),
    ...(draft.delegate_employee_id !== "" && {
      delegate_employee_id: draft.delegate_employee_id,
    }),
    ...(draft.travel_leave_days !== undefined &&
      draft.travel_leave_days > 0 && { travel_leave_days: draft.travel_leave_days }),
  };
}

function fileCreateFingerprint(input: FileLeaveInput): string {
  return JSON.stringify([
    input.policy_id,
    input.start_date,
    input.end_date,
    input.partial_days ?? null,
    input.reason ?? null,
    input.delegate_employee_id ?? null,
    input.travel_leave_days ?? null,
  ]);
}

export function LeaveFilePage(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  // The route is in no menu, so the tab strip has no label for it and fell back
  // to its generic "Kayıt" (E40); name it after the form it holds.
  useTabTitle(t("leave.form.title"));

  // Faz 8.6 / A3 — the mockup's "İzin bakiyesi" side card (emp-request-forms
  // .jsx): the requester's OWN balance rows from the live GET /v1/leave/
  // balances first page. `mine=true` makes the server scope it to the caller;
  // the local employee-id filter remains a defensive display guard. Convenience
  // summary only — the full surface is /me/leave/balance.
  const me = useQuery({ queryKey: ["me", "identity"], queryFn: fetchMe });
  const balances = useQuery({
    queryKey: ["leave", "balances", "side", "mine"],
    queryFn: () => fetchLeaveBalancesPage(null, true),
  });
  const myEmployeeId = me.data?.employee_id ?? null;
  const myBalanceRows = (balances.data?.items ?? []).filter(
    (row) => row.employee_id === myEmployeeId,
  );
  const myBalances = myBalanceRows.slice(0, 4);

  // The policy picker's option set (D1a): the balance rows ARE the answer to
  // "which policies may I draw from" — one policy can hold several balance
  // rows, so dedupe by policy_id and keep the first row's resolved name.
  // The tenant's travel-leave allowance per policy (LV-10). A failed or slow catalogue
  // read must not gate filing a leave, so it degrades to "not offered": no field, the
  // server still enforces the limit.
  const leaveTypes = useQuery({
    queryKey: ["leave", "types", "active"],
    queryFn: () => fetchLeaveTypes("active"),
  });
  const travelMaxByPolicy = new Map(
    (leaveTypes.data ?? []).map((type) => [type.platform_policy_id, type.travel_leave_max_days]),
  );
  const policyOptions = [
    ...new Map(
      myBalanceRows.map((row) => {
        const travelLeaveMaxDays = travelMaxByPolicy.get(row.policy_id) ?? 0;
        return [
          row.policy_id,
          {
            value: row.policy_id,
            label: row.policy_name ?? shortId(row.policy_id),
            ...(travelLeaveMaxDays > 0 && { travelLeaveMaxDays }),
          },
        ] as const;
      }),
    ).values(),
  ];

  // Delegate candidates (gap §9.1) — the company directory minus the requester;
  // delegating to yourself is the same as no delegate. The DIRECTORY, not the
  // personnel list: for a plain employee that list is one row (their own), which
  // this filter then emptied, so nobody could be picked as a delegate (E27).
  const directory = useQuery({
    queryKey: [...DIRECTORY_KEY, "picker"],
    queryFn: fetchDirectoryFirstPage,
  });
  const delegateOptions = (directory.data?.items ?? [])
    .filter((e) => e.employee_id !== myEmployeeId)
    .map((e) => ({
      value: e.employee_id,
      label: `${e.legal_first_name} ${e.legal_last_name}`.trim(),
    }));

  const [overlaps, setOverlaps] = useState<readonly ConflictOverlap[] | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [submittedOk, setSubmittedOk] = useState(false);

  // ResourceForm's inputs are uncontrolled and it exposes no reset, so a new
  // key is how a filed request stops being resubmittable: the in-flight guard
  // only covers the mutation window, and once it resolved the still-populated
  // form filed a byte-identical duplicate on the next click. Bumped on SUCCESS
  // only — a failed submit must keep what the user typed.
  const [formGeneration, setFormGeneration] = useState(0);

  // A submit failure retains the known DRAFT only for the exact same normalized
  // form body. A changed form clears that actionable state and starts a new
  // logical request, so an edited retry can never silently submit stale data.
  const pendingDraft = useRef<PendingDraft | null>(null);

  // A create response can be lost after the server has persisted the DRAFT,
  // leaving no request id to resume. Retain one key for that exact body so
  // the next attempt replays server-side; a changed body is a new action and
  // intentionally gets a new key. The key is cleared only once file→submit
  // succeeds, alongside the pending DRAFT id above.
  const pendingFileCreate = useRef<PendingFileCreate | null>(null);

  // A submit response can also be lost after the transition commits. Keep this
  // request-specific key until the submit succeeds so a retry replays the same
  // logical transition instead of hitting an already-submitted 409.
  const pendingSubmit = useRef<PendingSubmit | null>(null);

  // File-then-submit: a single user action files the draft then immediately
  // submits it so the requester sees the conflict warning in one step.
  const mutation = useMutation({
    mutationFn: async (draft: LeaveRequestDraft) => {
      const input = toFileLeaveInput(draft);
      const fingerprint = fileCreateFingerprint(input);
      if (pendingDraft.current !== null && pendingDraft.current.fingerprint !== fingerprint) {
        pendingDraft.current = null;
        pendingFileCreate.current = null;
        pendingSubmit.current = null;
      }

      let requestId = pendingDraft.current?.requestId ?? null;
      if (requestId === null) {
        if (pendingFileCreate.current?.fingerprint !== fingerprint) {
          pendingFileCreate.current = {
            fingerprint,
            idempotencyKey: crypto.randomUUID(),
          };
        }
        const created = await fileLeaveRequest(input, pendingFileCreate.current.idempotencyKey);
        requestId = created.request_id;
        pendingDraft.current = { requestId, fingerprint };
      }
      const submit =
        pendingSubmit.current?.requestId === requestId
          ? pendingSubmit.current
          : { requestId, idempotencyKey: crypto.randomUUID() };
      pendingSubmit.current = submit;
      return submitLeaveRequest(requestId, submit.idempotencyKey);
    },
    onSuccess: (result) => {
      pendingDraft.current = null;
      pendingFileCreate.current = null;
      pendingSubmit.current = null;
      setErrorKey(null);
      setSubmittedOk(true);
      setFormGeneration((n) => n + 1);
      setOverlaps(result.conflict.has_conflict ? result.conflict.overlaps : null);
      void queryClient.invalidateQueries({ queryKey: ["leave", "requests"] });
    },
    onError: (error) => {
      setSubmittedOk(false);
      setOverlaps(null);
      setErrorKey(errorMessage(error, t));
    },
  });

  // novem form scaffold (Faz 8.6 / A1 — emp-request-forms.jsx FormScaffold +
  // emp-requests.jsx REQ_INFO port): back link to the requester's own queue,
  // form on the left, the "Bilgi" side card on the right. Data path unchanged
  // (file → submit → conflict warning).
  return (
    <div className="mx-auto max-w-[1100px] space-y-5">
      <RouterLink
        to="/me/leave"
        className="inline-flex items-center gap-1.5 font-sans text-[13px] text-[var(--fg-secondary)] no-underline hover:text-[var(--fg-primary)]"
      >
        <ChevronLeft size={14} aria-hidden="true" />
        {t("leave.side.back")}
      </RouterLink>

      <div className="grid items-start gap-5 lg:grid-cols-[1fr_320px]">
        <div className="space-y-5">
          {submittedOk && overlaps === null && (
            <Alert severity="success" title={t("leave.file.submitted_title")}>
              {t("leave.file.submitted_description")}
            </Alert>
          )}
          {overlaps !== null && (
            <Alert severity="warning" title={t("leave.conflict.warning_title")}>
              {t("leave.conflict.warning_description")}
              <ul className="mt-2 list-disc pl-5">
                {overlaps.map((o) => (
                  <li key={o.request_id}>
                    {t("leave.conflict.overlap_range", {
                      start: o.start_date,
                      end: o.end_date,
                    })}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          {errorKey !== null && (
            <Alert severity="danger" title={t("leave.file.error_title")}>
              {errorKey}
            </Alert>
          )}

          <LeaveFormContent
            key={formGeneration}
            policyOptions={policyOptions}
            delegateOptions={delegateOptions}
            onPreview={(draft) => previewLeaveRequest(toFileLeaveInput(draft))}
            onSubmit={(draft): void => {
              mutation.mutate(draft);
            }}
          />
        </div>

        <div className="space-y-5">
          {/* "İzin bakiyesi" side card — emp-request-forms.jsx (own rows only). */}
          <Card>
            <CardBody>
              <div className="mb-3 font-sans text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--fg-muted)]">
                {t("leave.side.balance_title")}
              </div>
              {myBalances.length > 0 ? (
                <div className="mb-3 flex flex-col">
                  {myBalances.map((row, i) => (
                    <div
                      key={row.balance_id}
                      className={`flex items-center justify-between gap-3 py-2 ${
                        i > 0 ? "border-t border-[var(--border-subtle)]" : "pt-0"
                      }`}
                    >
                      <span className="font-sans text-[12.5px] text-[var(--fg-secondary)]">
                        {row.policy_name ?? shortId(row.policy_id)}
                      </span>
                      <span className="whitespace-nowrap font-sans text-[13px] font-semibold text-[var(--accent)]">
                        {formatLeaveSpan(row.balance_hours, i18n.language, t) ??
                          t("common.unknown")}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mb-3 mt-0 font-serif text-[12.5px] italic text-[var(--fg-muted)]">
                  {t("leave.side.balance_empty")}
                </p>
              )}
              <RouterLink
                to="/me/leave/balance"
                className="inline-flex items-center gap-1 font-sans text-[12.5px] font-medium text-[var(--accent)] no-underline hover:text-[var(--accent-hover)]"
              >
                {t("leave.side.balance_link")}
                <ChevronRight size={13} aria-hidden="true" />
              </RouterLink>
            </CardBody>
          </Card>

          {/* "Bilgi" side card — emp-requests.jsx REQ_INFO.leave bullets. */}
          <Card>
            <CardBody>
              <div className="mb-3 font-sans text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--fg-muted)]">
                {t("leave.side.title")}
              </div>
              <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                {(
                  [
                    [CalendarCheck2, t("leave.side.balance")],
                    [Clock3, t("leave.side.conflict")],
                    [UserRound, t("leave.side.delegate")],
                  ] as const
                ).map(([IconCmp, text]) => (
                  <li
                    key={text}
                    className="flex items-start gap-2.5 font-sans text-[12.5px] leading-normal text-[var(--fg-secondary)]"
                  >
                    <IconCmp
                      size={14}
                      aria-hidden="true"
                      className="mt-0.5 shrink-0 text-[var(--accent)]"
                    />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
